/** @jsx jsx */
import { jsx } from '@emotion/react';

import { useEffect } from 'react';

import Imgix from 'js/components/Imgix';

import BaseSelectQuestion from 'bundles/onboarding-common/components/modular/BaseSelectQuestion';
import { QUESTION_IDS } from 'bundles/onboarding-common/constants/questionScheme';
import { useOnboardingQuestionData } from 'bundles/onboarding-common/contexts/OnboardingQuestionDataContext';
import useArtificiallyPaginatedRoles from 'bundles/onboarding-common/hooks/useArtificiallyPaginatedRoles';
import useGetDefaultSavedRoles from 'bundles/onboarding-common/hooks/useGetDefaultSavedRoles';
import useRoleSelectQuestionState from 'bundles/onboarding-common/hooks/useRoleSelectQuestionState';
import type { RoleFragment } from 'bundles/onboarding-common/queries/__generated__/RoleFragment';
import type { RoleWithState } from 'bundles/onboarding-common/types';

import _t from 'i18n!nls/onboarding-common';

const RoleSelectQuestion = ({ defaultValue, roles }: { defaultValue: RoleWithState[]; roles: RoleFragment[] }) => {
  const {
    containerRef,
    rolesPerRow,
    iDontKnowRole,
    handleClick,
    onViewMoreClick,
    onViewAllClick,
    isLoading,
    isExpanded,
  } = useRoleSelectQuestionState({ defaultValue, roles });

  return (
    <BaseSelectQuestion
      itemsPerRow={rolesPerRow}
      containerRef={containerRef}
      handleClick={handleClick}
      onViewMoreClick={onViewMoreClick}
      onViewAllClick={onViewAllClick}
      isLoading={isLoading}
      isExpanded={isExpanded}
      getLabel={(item) => item.name || ''}
      getIcon={(item) =>
        item.id !== iDontKnowRole?.id ? (
          <Imgix src={item.onboardingRoleOptionImage?.url || ''} width={16} height={16} alt="" />
        ) : undefined
      }
      ariaLabel={_t('Select your role(s)')}
    />
  );
};

const RoleSelectQuestionWrapper = () => {
  const { setQuestionValue } = useOnboardingQuestionData();
  const { defaultValue, isLoadingDefaultRoles } = useGetDefaultSavedRoles();
  const { roles, loading: isLoadingRoles } = useArtificiallyPaginatedRoles({ skip: 0, limit: 20 });

  useEffect(() => {
    if (defaultValue) {
      setQuestionValue({
        __typename: QUESTION_IDS.roleSelect,
        value: defaultValue.map((role) => ({
          id: role.id,
          name: role.name || '',
          type: role.type,
        })),
      });
    }
  }, [defaultValue, setQuestionValue]);

  if (isLoadingDefaultRoles || isLoadingRoles || !roles || !defaultValue) {
    // TODO: Add a loading state
    return null;
  }

  return <RoleSelectQuestion defaultValue={defaultValue} roles={roles} />;
};

export default RoleSelectQuestionWrapper;
